'use client'

import { useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  RotateCcw,
  Target,
  Trophy,
  Volume2,
  XCircle
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useSettings } from '@/hooks/useSettings'
import { useChineseFont } from '@/lib/chinese-font'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import type { PronunciationScript } from '@/content/pronunciation'
import {
  buildQuizQuestions,
  scoreQuizAnswer,
  type PronunciationQuizMode,
  type PronunciationQuizQuestion
} from '@/lib/pronunciation/quiz'
import {
  getWeakSoundTags,
  readPronunciationProgress,
  recordPronunciationQuizResult,
  writePronunciationProgress,
  type PronunciationAnswerResult
} from '@/lib/pronunciation/progress'
import { cn } from '@/lib/utils'
import { PronunciationAudioButton } from './pronunciation-audio-button'
import { playAudio, stopAudio } from '@/lib/pronunciation/audio-player'

const TARGET_SCORE = 60
const CORRECT_POINTS = 6
const WRONG_POINTS = 4
const NEXT_DELAY_MS = 850

type PronunciationQuizProps = {
  script: PronunciationScript
  lessonId: string
  title: string
  mode?: PronunciationQuizMode
  confusionTag?: string
  buttonLabel?: string
  buttonClassName?: string
}

type AnswerState = PronunciationAnswerResult & {
  questionId: string
}

export function PronunciationQuiz({
  script,
  lessonId,
  title,
  mode = 'lesson',
  confusionTag,
  buttonLabel = 'Practice',
  buttonClassName
}: PronunciationQuizProps) {
  const [open, setOpen] = useState(false)
  const [questions, setQuestions] = useState<PronunciationQuizQuestion[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null)
  const [answers, setAnswers] = useState<AnswerState[]>([])
  const [score, setScore] = useState(0)
  const [finished, setFinished] = useState(false)
  const [activeMode, setActiveMode] = useState<PronunciationQuizMode>(mode)
  const [roundSeed, setRoundSeed] = useState(0)

  const [tempSelectedId, setTempSelectedId] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)
  const [showExitConfirm, setShowExitConfirm] = useState(false)

  const currentQuestion = questions[currentIndex]
  const selectedIsCorrect =
    currentQuestion && selectedOptionId
      ? scoreQuizAnswer(currentQuestion, selectedOptionId)
      : null
  const correctCount = answers.filter((answer) => answer.correct).length

  const progressPercent = Math.min(100, (score / TARGET_SCORE) * 100)

  const start = (nextMode: PronunciationQuizMode = mode) => {
    const seed = Date.now()
    setRoundSeed(seed)
    setQuestions(buildQuestions(nextMode, seed))
    setActiveMode(nextMode)
    setCurrentIndex(0)
    setSelectedOptionId(null)
    setTempSelectedId(null)
    setChecked(false)
    setAnswers([])
    setScore(0)
    setFinished(false)
  }

  const buildQuestions = useMemo(
    () => (nextMode: PronunciationQuizMode, seed: number) => {
      const progress = readPronunciationProgress()
      const weakSoundTags = getWeakSoundTags(progress, script)
      return buildQuizQuestions({
        lessonId,
        script,
        mode: nextMode,
        confusionTag,
        weakSoundTags,
        count: 12,
        seed: `${script}:${lessonId}:${nextMode}:${seed}`
      })
    },
    [confusionTag, lessonId, script]
  )

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    if (!questions.length) start(mode)
    return () => {
      document.body.style.overflow = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    return () => {
      stopAudio()
    }
  }, [])

  useEffect(() => {
    if (!open) {
      stopAudio()
    }
  }, [open])

  const choose = (optionId: string) => {
    if (!currentQuestion || selectedOptionId || finished) return
    const correct = scoreQuizAnswer(currentQuestion, optionId)
    const nextScore = Math.max(
      0,
      score + (correct ? CORRECT_POINTS : -WRONG_POINTS)
    )
    const nextAnswer = {
      questionId: currentQuestion.id,
      lessonId,
      correct,
      soundTags: currentQuestion.soundTags
    }
    const nextAnswers = [...answers, nextAnswer]

    setSelectedOptionId(optionId)
    setScore(nextScore)
    setAnswers(nextAnswers)
  }

  const advance = () => {
    if (score >= TARGET_SCORE) return finish()

    if (currentIndex >= questions.length - 1) {
      const seed = roundSeed + 1
      setRoundSeed(seed)
      setQuestions((current) => [
        ...current,
        ...buildQuestions(activeMode, seed)
      ])
    }

    setCurrentIndex((index) => index + 1)
    setSelectedOptionId(null)
    setTempSelectedId(null)
    setChecked(false)
  }

  const finish = (finalAnswers = answers, finalScore = score) => {
    if (finished) return
    const progress = readPronunciationProgress()
    const nextProgress = recordPronunciationQuizResult({
      progress,
      script,
      lessonId,
      answers: finalAnswers,
      completeLesson: activeMode === 'lesson' && finalScore >= TARGET_SCORE
    })
    writePronunciationProgress(nextProgress)
    setFinished(true)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && answers.length > 0 && !finished) {
      setShowExitConfirm(true)
    } else {
      setOpen(nextOpen)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button type="button" className={buttonClassName}>
            <Target className="h-4 w-4" />
            {buttonLabel}
          </Button>
        </DialogTrigger>
        <DialogContent
          className="!inset-0 !top-0 !left-0 !h-dvh !max-h-dvh !w-screen !max-w-none !translate-x-0 !translate-y-0 overflow-hidden rounded-none border-0 p-0"
          showCloseButton={false}
        >
          <div className="bg-background flex h-dvh flex-col">
            <header className="flex min-h-16 items-center justify-between gap-3 border-b px-4 md:px-6">
              <div className="min-w-0">
                <DialogTitle className="truncate text-base font-semibold md:text-lg">
                  {title}
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-xs">
                  Reach {TARGET_SCORE} points to finish.
                </DialogDescription>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </header>

            <div className="border-b px-4 py-3 md:px-6">
              <div className="mb-2 flex items-center justify-between text-xs font-medium">
                <span>{score} points</span>
                <span>
                  {correctCount}/{answers.length || 0} correct
                </span>
              </div>
              <Progress value={progressPercent} size="sm" />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 md:px-6 md:py-8">
              {currentQuestion && !finished && (
                <QuestionView
                  question={currentQuestion}
                  selectedOptionId={selectedOptionId}
                  selectedIsCorrect={selectedIsCorrect}
                  tempSelectedId={tempSelectedId}
                  setTempSelectedId={setTempSelectedId}
                  checked={checked}
                  setChecked={setChecked}
                  setSelectedOptionId={setSelectedOptionId}
                  choose={choose}
                  advance={advance}
                  score={score}
                />
              )}

              {finished && (
                <FinishedView
                  score={score}
                  correctCount={correctCount}
                  answerCount={answers.length}
                  onRetry={() => start(activeMode)}
                  onWeak={() => start('weak')}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>Quit quiz?</DialogTitle>
          <DialogDescription>
            Are you sure you want to quit? Your progress in this session will
            not be saved.
          </DialogDescription>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowExitConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setShowExitConfirm(false)
                setOpen(false)
              }}
            >
              Quit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function QuestionView({
  question,
  selectedOptionId,
  selectedIsCorrect,
  tempSelectedId,
  setTempSelectedId,
  checked,
  setChecked,
  setSelectedOptionId,
  choose,
  advance,
  score
}: {
  question: PronunciationQuizQuestion
  selectedOptionId: string | null
  selectedIsCorrect: boolean | null
  tempSelectedId: string | null
  setTempSelectedId: (id: string | null) => void
  checked: boolean
  setChecked: (checked: boolean) => void
  setSelectedOptionId: (id: string | null) => void
  choose: (optionId: string) => void
  advance: () => void
  score: number
}) {
  const { settings } = useSettings()
  const chineseFont = useChineseFont()

  const displayHanzi =
    settings.scriptPriority === 'traditional'
      ? question.item.traditional || question.item.hanzi
      : question.item.hanzi

  const isAudioToScript = question.type === 'word-audio-to-script'

  const handleActionClick = () => {
    if (!checked) {
      if (tempSelectedId) {
        setChecked(true)
        setSelectedOptionId(tempSelectedId)
        choose(tempSelectedId)
      }
    } else {
      advance()
    }
  }

  const actionButtonLabel = !checked
    ? 'Check'
    : score >= TARGET_SCORE
      ? 'Finish'
      : 'Next'

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      {/* Question Card */}
      <div className="bg-card relative flex flex-col items-center justify-center overflow-hidden rounded-3xl border p-6 text-center shadow-sm md:p-8">
        {/* Decorative corner element */}
        <div className="from-primary/5 pointer-events-none absolute top-0 right-0 h-20 w-20 rounded-bl-full bg-linear-to-bl to-transparent" />

        {/* Emoji Display (Large, centered) */}
        {question.item.emoji && (
          <span className="mb-3 text-6xl select-none">
            {question.item.emoji}
          </span>
        )}

        {/* Chinese Characters (Large) */}
        <h2
          className={cn(
            chineseFont,
            'text-foreground mt-2 text-5xl font-bold tracking-tight'
          )}
        >
          {displayHanzi}
        </h2>

        {/* Translation / Meaning (Larger, prominent) */}
        <p className="text-muted-foreground mt-3 max-w-md text-lg leading-relaxed font-medium">
          {question.item.meaning}
        </p>

        {/* Script / Audio Trigger */}
        <div className="mt-6 flex min-h-[44px] items-center justify-center">
          {isAudioToScript ? (
            <div className="flex items-center gap-4">
              <PronunciationAudioButton
                audioPath={question.item.audioPath}
                label={question.item.pinyin}
                autoPlayKey={question.id}
                compact={true}
                className="h-11 w-11 shadow-md transition-transform hover:scale-105"
              />
              {checked && (
                <div className="text-foreground animate-fade-in flex items-baseline gap-2 text-xl font-semibold">
                  <span>
                    {question.script === 'pinyin'
                      ? question.item.pinyin
                      : question.item.zhuyin}
                  </span>
                  {question.script === 'pinyin' && (
                    <span className="text-muted-foreground text-sm font-normal">
                      ({question.item.zhuyin})
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-foreground bg-muted/40 flex items-baseline gap-2 rounded-full px-4 py-1.5 text-2xl font-bold select-none">
              <span>
                {question.script === 'pinyin'
                  ? question.item.pinyin
                  : question.item.zhuyin}
              </span>
              {question.script === 'pinyin' && (
                <span className="text-muted-foreground text-sm font-medium">
                  ({question.item.zhuyin})
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Option Cards */}
      <div className="grid grid-cols-2 gap-3">
        {question.options.map((option, optionIndex) => {
          const isSelectedBeforeCheck = !checked && tempSelectedId === option.id
          const isSelectedAfterCheck = checked && selectedOptionId === option.id
          const isCorrectAnswer = option.id === question.correctOptionId
          const revealCorrect = checked && isCorrectAnswer

          const handleChoice = () => {
            if (question.type === 'script-to-audio' && option.audioPath) {
              playAudio(option.audioPath)
            }
            if (!checked) {
              setTempSelectedId(option.id)
            }
          }

          return (
            <button
              key={option.id}
              type="button"
              onClick={handleChoice}
              className={cn(
                'bg-card focus-visible:ring-ring flex min-h-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border p-4 text-center shadow-xs transition-[border-color,background-color,ring,opacity] outline-none focus-visible:ring-2',
                !checked && 'hover:bg-muted/45',
                isSelectedBeforeCheck &&
                  'border-primary bg-primary/5 ring-primary/20 ring-2',
                isSelectedAfterCheck &&
                  selectedIsCorrect &&
                  'border-emerald-500 bg-emerald-500/10',
                isSelectedAfterCheck &&
                  !selectedIsCorrect &&
                  'border-destructive bg-destructive/10',
                revealCorrect &&
                  !isSelectedAfterCheck &&
                  'border-emerald-500/70 bg-emerald-500/5',
                checked &&
                  !isSelectedAfterCheck &&
                  !revealCorrect &&
                  'border-muted/20 opacity-50'
              )}
            >
              {isAudioToScript ? (
                <span className="block text-2xl font-semibold">
                  {option.label}
                </span>
              ) : (
                <>
                  <Volume2
                    className={cn(
                      'text-muted-foreground h-8 w-8 transition-transform',
                      !checked && 'hover:text-foreground hover:scale-110'
                    )}
                  />
                  {checked ? (
                    <span className="animate-fade-in mt-1 text-sm font-semibold">
                      {option.label}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs font-medium">
                      Option {optionIndex + 1}
                    </span>
                  )}
                </>
              )}
            </button>
          )
        })}
      </div>

      {/* Unified Action Button */}
      <div className="mt-4 pt-1">
        <Button
          type="button"
          onClick={handleActionClick}
          disabled={!tempSelectedId}
          className="h-12 w-full rounded-2xl text-base font-semibold"
        >
          {actionButtonLabel}
        </Button>
      </div>

      {/* Correct / Incorrect Inline Status */}
      {checked && (
        <div
          className={cn(
            'animate-fade-in flex items-center justify-center gap-2 text-sm font-semibold',
            selectedIsCorrect ? 'text-emerald-600' : 'text-destructive'
          )}
        >
          {selectedIsCorrect ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          {selectedIsCorrect ? 'Correct' : 'Not quite'}
        </div>
      )}
    </div>
  )
}

function FinishedView({
  score,
  correctCount,
  answerCount,
  onRetry,
  onWeak
}: {
  score: number
  correctCount: number
  answerCount: number
  onRetry: () => void
  onWeak: () => void
}) {
  return (
    <div className="mx-auto flex min-h-[calc(100dvh-12rem)] w-full max-w-3xl flex-col justify-center">
      <div className="bg-card rounded-3xl border p-6 shadow-sm md:p-8">
        <Trophy className="text-primary h-10 w-10" />
        <h2 className="mt-5 text-3xl font-semibold tracking-tight">
          Lesson complete.
        </h2>
        <p className="text-muted-foreground mt-2 text-sm">
          {score} points. {correctCount} of {answerCount} correct.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button type="button" onClick={onRetry}>
            <RotateCcw className="h-4 w-4" />
            Practice again
          </Button>
          <Button type="button" variant="outline" onClick={onWeak}>
            Weak sounds
          </Button>
        </div>
      </div>
    </div>
  )
}
